import { chatKeys } from '~/lib/query-keys';

/**
 * FRONT-2530: Eddy's `chat_sessions_delete` tool moves chats to the Recycle
 * bin (or purges them) server-side, and the chat routes publish no event for
 * it — so every chat list the person has open (Chats page, top-bar activity,
 * Recycle bin) would otherwise stay stale until the 15s poll or the end of
 * the stream. This pure mapping says which query prefixes a finished tool
 * call makes stale; `useAgentChat` applies it the moment the tool part
 * reaches `output-available`.
 *
 * Every chat list, the Recycle bin (`useChatList({ states: ['trashed'] })`)
 * and the per-thread lifecycle row all live under `chatKeys.threadsRoot()`,
 * so that one prefix covers them. Only the chats a purge actually removed
 * also drop their own cached history (every branch of that session id); the
 * whole `history` segment is never invalidated, which would refetch the
 * transcript of every chat the person has open for no reason.
 */
export const CHAT_SESSIONS_DELETE_TOOL = 'chat_sessions_delete';

function parseOutput(output: unknown): Record<string, unknown> | null {
  if (typeof output === 'string') {
    try {
      const parsed: unknown = JSON.parse(output);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (output && typeof output === 'object' && !Array.isArray(output)) {
    return output as Record<string, unknown>;
  }
  return null;
}

function nonEmpty(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

/** Query-key prefixes to invalidate for one finished tool call; empty = none. */
export function chatQueryKeysForToolResult(
  toolName: string,
  output: unknown,
): ReadonlyArray<readonly unknown[]> {
  if (toolName !== CHAT_SESSIONS_DELETE_TOOL) return [];
  const result = parseOutput(output);
  if (!result) return [];
  // Error, deferred-approval and all-skipped results carry neither array
  // populated — nothing changed server-side, so nothing is stale.
  if (!nonEmpty(result.trashed) && !nonEmpty(result.purged)) return [];
  const keys: Array<readonly unknown[]> = [chatKeys.threadsRoot()];
  const purged = Array.isArray(result.purged) ? result.purged : [];
  for (const ref of purged) {
    const sessionId = ref && typeof ref === 'object' ? (ref as { sessionId?: unknown }).sessionId : undefined;
    if (typeof sessionId === 'string' && sessionId) keys.push([...chatKeys.all, 'history', sessionId]);
  }
  return keys;
}
